import { CellTypes, RequiredColumnsEnum } from './pricing-model-table.enum';

/** The shape this reads off a pricing-model table: just the column properties that decide fillability. */
export interface EditableInputColumn {
  id: string;
  isReadOnly?: boolean;
  isHidden?: boolean;
  type?: CellTypes;
}

/**
 * Whether a column is something a caller can fill: not read-only (the amount), not hidden (the derived
 * quantity on a fixed model), and not a formula — a formula column is computed from the other cells, and
 * tableCalculation overwrites whatever was written there on publish. This is what the UI renders as
 * editable cells, so a caller sees the same inputs a person would.
 */
export function isFillableColumn(column: EditableInputColumn): boolean {
  return !column.isReadOnly && !column.isHidden && column.type !== CellTypes.FORMULA;
}

/**
 * Maps a table column to the row field a caller sets it with.
 *
 * The two vocabularies differ, and advertising the column ids instead of these names is actively
 * misleading: `item` is not a row field at all (it is the line item's `itemName`), and the visible count
 * column is `usage` internally but `quantity` on the wire. A caller that copies a column id gets rejected
 * by the row schema for a field the API told it to send.
 */
export const ROW_FIELD_BY_COLUMN: Record<string, string | undefined> = {
  [RequiredColumnsEnum.item]: undefined,
  [RequiredColumnsEnum.price]: 'price',
  [RequiredColumnsEnum.quantity]: 'quantity',
  usage: 'quantity',
  from: 'from',
  to: 'to',
  packageSize: 'packageSize',
};

/**
 * The row fields a pricing model accepts, named as a caller sends them — read off the model's own columns
 * and translated through ROW_FIELD_BY_COLUMN. Deduplicated, since `usage` and `quantity` both map to
 * `quantity`, and a column with no row field (the item) is omitted.
 *
 * `quantity` is dropped for a pay-as-you-go model even though the column is there: the quantity comes from
 * reported usage, which is the whole point of pay-as-you-go, and the UI shows the metered item in that
 * cell rather than an input. Advertising it would invite a caller to send a commitment that is never
 * billed as one.
 *
 * Lives here rather than in the contracts service so the UI and the API answer this the same way: the two
 * drifting is what makes a caller fill a cell the table then overwrites.
 */
export function editableInputsOf(table?: { columns?: EditableInputColumn[] }, isPayAsYouGo?: boolean): string[] {
  const fields = (table?.columns ?? [])
    .filter(isFillableColumn)
    .map((column) => ROW_FIELD_BY_COLUMN[column.id])
    .filter((field): field is string => !!field)
    .filter((field) => !(isPayAsYouGo && field === 'quantity'));
  return [...new Set(fields)];
}
